import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../utils/apiConfig';
import { PAYMENT_MODES, BOOKING_SOURCES, TIME_SLOTS, apiErrMsg, normalizeCourt } from '../../utils/bookingFormatters';
import { ConflictCard } from './ConflictCard';

export function EditBookingDialog({ booking, onClose, onSuccess, getVenueNames, getGamesForVenue, getCourtsForVenueGame }) {
  const VENUES = getVenueNames();

  // Resolve the booking's stored court to the matching master court name so the
  // dropdown displays it (handles "Court 2"->"Court-2", "Court-7"->"Court-7 / Centre
  // Court", case/format diffs). Falls back to the raw value if no match.
  const resolveCourtName = (raw, options) => {
    if (!raw) return raw || '';
    const norm = normalizeCourt(raw).toLowerCase();
    const rawLower = raw.toLowerCase();
    const hit = (options || []).find((o) => {
      const ol = o.toLowerCase();
      return ol === rawLower || ol === norm || normalizeCourt(o).toLowerCase() === norm || ol.startsWith(norm + ' ') || ol.startsWith(norm + '/');
    });
    return hit || raw;
  };

  const initialCourt = resolveCourtName(booking.court, getCourtsForVenueGame(booking.venue, booking.game));

  const [formData, setFormData] = useState({
    venue: booking.venue,
    game: booking.game,
    court: initialCourt,
    date: booking.date,
    start_time: booking.start_time,
    end_time: booking.end_time,
    player_name: booking.player_name,
    player_mobile: booking.player_mobile,
    amount: booking.amount || 0,
    advance_payment: booking.advance_payment || 0,
    payment_mode: booking.payment_mode,
    booking_source: booking.booking_source || '',
    discount_amount: booking.discount_amount || 0,
    remarks: booking.remarks || '',
  });
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState(null);

  // Court options: the venue+game's active courts, plus the current court if it
  // isn't in that list (inactive/legacy) so it always remains visible & selectable.
  const venueCourts = (formData.venue && formData.game) ? getCourtsForVenueGame(formData.venue, formData.game) : [];
  const courtOptions = formData.court && !venueCourts.includes(formData.court)
    ? [formData.court, ...venueCourts]
    : venueCourts;

  const totalAmount = parseFloat(formData.amount) || 0;
  const discountAmount = parseFloat(formData.discount_amount) || 0;
  const advancePayment = parseFloat(formData.advance_payment) || 0;
  const pendingAmount = Math.max(0, totalAmount - advancePayment);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setConflict(null);
    try {
      await axios.put(`${API}/bookings/${booking.id}`, {
        ...formData,
        amount: totalAmount,
        discount_amount: discountAmount,
        advance_payment: advancePayment,
        pending_amount: pendingAmount,
      });
      toast.success('Booking updated successfully');
      onSuccess();
    } catch (error) {
      console.error('Failed to update booking:', error);
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'overlapping_booking') {
        setConflict(detail.conflict);
        toast.error(detail.message || 'Time slot not available');
      } else {
        toast.error(apiErrMsg(error, 'Failed to update booking'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Booking</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="edit_venue">Venue</Label>
              <Select value={formData.venue} onValueChange={(value) => setFormData({ ...formData, venue: value })}>
                <SelectTrigger data-testid="edit-venue"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VENUES.map((v) => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_game">Game</Label>
              <Select value={formData.game} onValueChange={(value) => setFormData({ ...formData, game: value, court: '' })} disabled={!formData.venue}>
                <SelectTrigger data-testid="edit-game"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {getGamesForVenue(formData.venue).map((g) => (<SelectItem key={g} value={g}>{g}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_court">Court</Label>
              <Select value={formData.court} onValueChange={(value) => setFormData({ ...formData, court: value })} disabled={!formData.game}>
                <SelectTrigger data-testid="edit-court"><SelectValue placeholder="Select court" /></SelectTrigger>
                <SelectContent>
                  {courtOptions.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_date">Date</Label>
              <Input id="edit_date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} data-testid="edit-date" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_start_time">Start Time</Label>
              <Select value={formData.start_time} onValueChange={(value) => setFormData({ ...formData, start_time: value })}>
                <SelectTrigger data-testid="edit-start-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_end_time">End Time</Label>
              <Select value={formData.end_time} onValueChange={(value) => setFormData({ ...formData, end_time: value })}>
                <SelectTrigger data-testid="edit-end-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((time) => (<SelectItem key={time} value={time}>{time}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_player_name">Player Name</Label>
              <Input id="edit_player_name" value={formData.player_name} onChange={(e) => setFormData({ ...formData, player_name: e.target.value })} required data-testid="edit-player-name" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_player_mobile">Mobile Number <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="edit_player_mobile" type="tel" value={formData.player_mobile} onChange={(e) => setFormData({ ...formData, player_mobile: e.target.value })} data-testid="edit-player-mobile" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_amount">Total Amount (₹)</Label>
              <Input id="edit_amount" type="number" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required min="0" step="0.01" data-testid="edit-amount" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_advance_payment">Advance Payment (₹)</Label>
              <Input id="edit_advance_payment" type="number" value={formData.advance_payment} onChange={(e) => setFormData({ ...formData, advance_payment: e.target.value })} min="0" step="0.01" data-testid="edit-advance-payment" />
            </div>

            <div className="col-span-2">
              <div className={`p-3 rounded-lg border ${pendingAmount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${pendingAmount > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                    {pendingAmount > 0 ? 'Pending Amount' : 'Fully Paid'}
                  </span>
                  <span className={`font-bold text-lg ${pendingAmount > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                    ₹{pendingAmount.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_payment_mode">Payment Mode</Label>
              <Select value={formData.payment_mode} onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}>
                <SelectTrigger data-testid="edit-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((mode) => (<SelectItem key={mode} value={mode}>{mode}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_booking_source">Booking Source</Label>
              <Select value={formData.booking_source} onValueChange={(value) => setFormData({ ...formData, booking_source: value })}>
                <SelectTrigger data-testid="edit-booking-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOOKING_SOURCES.map((source) => (<SelectItem key={source} value={source}>{source}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_discount_amount">Discount Amount (₹)</Label>
              <Input id="edit_discount_amount" type="number" value={formData.discount_amount} onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })} min="0" step="0.01" data-testid="edit-discount-amount" />
            </div>
          </div>

          {conflict && (
            <div data-testid="edit-conflict">
              <ConflictCard
                conflict={conflict}
                title="Slot not available"
                onNavigate={onClose}
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
            <Button type="submit" disabled={loading} className="bg-primary hover:bg-primary-hover" data-testid="edit-submit">
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
